// Display helpers. All amounts are stored as minor-unit integers (e.g. cents).
// Currencies like JPY have 0 decimals — lean on Intl.NumberFormat which knows.

// Minor-unit decimals for a currency (EUR→2, JPY→0, KWD→3), Intl-derived so
// storage scaling and display always agree. Unknown/missing codes default to 2.
export function currencyDecimals(currency: string | null | undefined): number {
  const ccy = (currency ?? "").trim().toUpperCase();
  if (!ccy) return 2;
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy })
      .resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function formatMoney(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor == null) return "—";
  const ccy = (currency ?? "").trim().toUpperCase();
  // No currency on a real amount is an upstream data bug, not USD. Show the value
  // without fabricating a symbol (this is a multi-currency, no-FX system).
  if (!ccy) {
    return new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100);
  }
  try {
    const fmt = new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy });
    const decimals = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    const major = minor / Math.pow(10, decimals);
    return fmt.format(major);
  } catch {
    // Unknown ISO code — keep the real code (never USD) and a neutral 2dp default.
    return `${(minor / 100).toFixed(2)} ${ccy}`;
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Date-only values (effective_from, granted_on, fx effective_date) parse as
  // UTC midnight — render them in UTC too, or a UTC-negative viewer sees the
  // previous day. Full timestamps keep local rendering.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return d.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    ...(dateOnly ? { timeZone: "UTC" } : {}),
  });
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate == null) return "—";
  return `${(Number(rate) * 100).toFixed(2)}%`;
}

export function yearOf(iso: string | null | undefined): number {
  if (!iso) return new Date().getUTCFullYear();
  return new Date(iso).getUTCFullYear();
}

// Sum minor units bucketed by currency. Returns an array of {currency, total}.
export function sumByCurrency<T extends { currency?: string | null }>(
  rows: T[],
  pick: (row: T) => number,
): { currency: string; total: number }[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    // Bucket missing currencies under "" rather than merging them into USD —
    // formatMoney renders "" as a plain number so the gap is visible, not masked.
    const ccy = (row.currency ?? "").trim().toUpperCase();
    map.set(ccy, (map.get(ccy) ?? 0) + pick(row));
  }
  return [...map.entries()].map(([currency, total]) => ({ currency, total }));
}

export function formatMoneyMulti(totals: { currency: string; total: number }[]): string {
  if (totals.length === 0) return "—";
  return totals.map((t) => formatMoney(t.total, t.currency)).join(" + ");
}
